/**
 * The eraser's own controls (SRS C4), shown while the eraser is in hand.
 *
 * Three ways of working, said in the words a teacher would use rather than in the
 * spec's, and one size. The size is a screen width, like the pen's thickness, so the
 * circle under the hand feels the same at every zoom.
 */

import type { EraserMode } from '@opal/shared';

import { MAX_ERASER_PX, MIN_ERASER_PX } from '../../board/erase.js';
import { useBoardStore } from '../../board/store.js';

interface ModeChoice {
  readonly value: EraserMode;
  readonly label: string;
  readonly hint: string;
}

const MODES: readonly ModeChoice[] = [
  { value: 'stroke', label: 'Whole line', hint: 'Rub over a line and all of it goes' },
  { value: 'area', label: 'Part of a line', hint: 'Rubs out only what the circle covers' },
  { value: 'object', label: 'Click to delete', hint: 'One click removes one thing' },
];

function ModePicker() {
  const mode = useBoardStore((state) => state.eraserMode);
  const setMode = useBoardStore((state) => state.setEraserMode);

  return (
    <div className="flex flex-col gap-1">
      {MODES.map(({ value, label, hint }) => {
        const active = mode === value;
        return (
          <button
            key={value}
            type="button"
            aria-pressed={active}
            title={hint}
            onClick={() => {
              setMode(value);
            }}
            className={`flex min-h-touch flex-col items-start justify-center rounded-md border px-3 py-1.5 text-left transition-colors duration-fast ${
              active
                ? 'border-primary bg-primary-subtle text-primary'
                : 'border-border bg-surface text-text-primary hover:bg-surface-hover'
            }`}
          >
            <span className="text-sm font-medium">{label}</span>
            <span className="text-xs text-text-secondary">{hint}</span>
          </button>
        );
      })}
    </div>
  );
}

function SizeSlider() {
  const width = useBoardStore((state) => state.eraserScreenWidth);
  const setWidth = useBoardStore((state) => state.setEraserScreenWidth);

  return (
    <div className="flex items-center gap-3">
      <input
        type="range"
        min={MIN_ERASER_PX}
        max={MAX_ERASER_PX}
        step={1}
        value={width}
        aria-label="Eraser size"
        onChange={(event) => {
          setWidth(Number(event.target.value));
        }}
        className="h-touch flex-1 accent-primary"
      />
      <span className="tabular w-12 text-right text-base text-text-primary">
        {Math.round(width)}px
      </span>
    </div>
  );
}

export function EraserControls() {
  return (
    <>
      <section className="flex flex-col gap-2">
        <h3 className="text-sm font-medium text-text-secondary">How it erases</h3>
        <ModePicker />
      </section>
      <section className="flex flex-col gap-2">
        <h3 className="text-sm font-medium text-text-secondary">Size</h3>
        <SizeSlider />
      </section>
    </>
  );
}
